import { StoryblokClient } from '@storyblok/js';
import { getPreviewToken } from './preview-auth';
import { canShareChurchUpdate } from './church-updates';
import { isInStoryblokVisualEditor } from './storyblok-visual-editor';

const storyblokClients = new Map();
const STORYBLOK_PREVIEW_FLAG = 'storyblokPreviewBypass';

// Rate limit retry configuration
const MAX_RETRIES = 4; // Reduced from 4 to 3 for faster failure
const INITIAL_RETRY_DELAY = 1000; // 100ms - very fast initial retry
const MAX_RETRY_DELAY = 30000; // 2 seconds max delay - much faster
const FAIL_FAST_ON_429 = false; // Set to true to skip retries and fail immediately on 429

// TEMPORARY: Testing flag to simulate 429 errors
// Set to true to test 429 error handling, false to disable
// Options: false (disabled), true (always fail all attempts), number (fail for first N attempts then succeed)
const SIMULATE_429_ERROR = false; // Change to true to test 429 errors (all attempts will fail)

/**
 * Check if current route is a preview route
 * Preview routes follow pattern: /preview/:id where id matches VUE_APP_PREVIEW_ID
 * @returns {boolean} True if current path is a preview route
 */
function isPreviewRoute() {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const pathname = window.location.pathname;
    const expectedId = process.env.VUE_APP_PREVIEW_ID;
    if (!expectedId) return false;
    // Check for /preview/:id pattern where id matches the expected preview ID
    const escapedId = expectedId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`^/preview/${escapedId}(/|$)`).test(pathname);
  } catch (_) {
    return false;
  }
}

/**
 * Check if we're in Storyblok preview mode (has preview token)
 * @returns {boolean} True if preview token exists in URL or session storage
 */
export function isStoryblokPreviewMode() {
  try {
    if (typeof window === 'undefined') return false;

    const params = new URLSearchParams(window.location.search);
    const hasPreviewParam =
      params.has('preview_token') ||
      params.has('previewToken') ||
      params.has('_storyblok_preview_tk') ||
      params.has('_storyblok_tk') ||
      params.has('_storyblok') ||
      params.has('_storyblok_tk[token]');

    const hasBypassFlag =
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(STORYBLOK_PREVIEW_FLAG) === 'true';

    return hasPreviewParam || hasBypassFlag;
  } catch (_) {
    return false;
  }
}

/**
 * Resolve which Storyblok access token to use
 * Preview authentication is handled via HTTP Basic Auth in PreviewLayout component
 * @param {Object} options - Token resolution options
 * @param {boolean} options.forcePreview - Force preview token usage
 * @param {boolean} options.ignoreUrlToken - Skip URL token fallback
 * @returns {string} Access token to use
 */
function resolveAccessToken({ forcePreview = false, ignoreUrlToken = false } = {}) {
  const accessToken = process.env.VUE_APP_STORYBLOK_TOKEN;
  const previewToken = process.env.VUE_APP_STORYBLOK_PREVIEW_TOKEN;
  const shouldUsePreviewToken = forcePreview || isStoryblokPreviewMode() || isPreviewRoute();

  if (!shouldUsePreviewToken) {
    return accessToken;
  }

  // Prioritize env token, then URL token
  if (previewToken?.trim()) {
    console.log('[Storyblok] Using preview token from environment');
    return previewToken;
  }

  // Fallback to URL token if env token not available
  if (!ignoreUrlToken && typeof window !== 'undefined') {
    const urlToken = getPreviewToken();
    if (urlToken?.trim()) {
      console.log('[Storyblok] Using preview token from URL (env token not available)');
      try {
        sessionStorage.setItem(STORYBLOK_PREVIEW_FLAG, 'true');
      } catch (_) {
        // Ignore sessionStorage errors (e.g., in private browsing mode)
      }
      return urlToken;
    }
  }

  console.warn(
    '[Storyblok] Preview token requested but neither VUE_APP_STORYBLOK_PREVIEW_TOKEN nor URL token available. Falling back to public token.',
  );
  return accessToken;
}

export function getStoryblokClient(forcePreviewOrOptions = false) {
  const options =
    typeof forcePreviewOrOptions === 'object'
      ? { forcePreview: false, ignoreUrlToken: false, ...forcePreviewOrOptions }
      : { forcePreview: !!forcePreviewOrOptions, ignoreUrlToken: false };

  const accessToken = resolveAccessToken(options);

  if (!accessToken) {
    console.error(
      '[Storyblok] Storyblok client cannot be initialized: No token found. ' +
        'Please set VUE_APP_STORYBLOK_TOKEN or provide a preview token.',
    );
    return null;
  }

  if (!storyblokClients.has(accessToken)) {
    // Build client options
    const clientOptions = {
      accessToken,
    };

    // Add region if specified (defaults to 'us' if not EU)
    // Set VUE_APP_STORYBLOK_REGION=eu for EU region, or leave unset for US
    const region = process.env.VUE_APP_STORYBLOK_REGION;
    if (region) {
      clientOptions.region = region;
      console.log(`[Storyblok] Using region: ${region}`);
    }

    storyblokClients.set(accessToken, new StoryblokClient(clientOptions));
    console.log(`[Storyblok] Client initialized (${options.forcePreview ? 'preview' : 'public'})`);
  }

  return storyblokClients.get(accessToken);
}

/**
 * Try fallback tokens when preview token fails
 * @param {boolean} mightHaveUsedUrlToken - Whether URL token might have been used
 * @param {Function} draftRequest - Function to make draft request with client
 * @param {Function} publishedRequest - Function to make published request with client
 * @param {string} type - Request type ('story' or 'stories') for logging
 * @returns {Promise<Object|null>} Response data or null if all fallbacks fail
 */
async function tryPreviewTokenFallback(
  mightHaveUsedUrlToken,
  draftRequest,
  publishedRequest,
  type,
) {
  console.warn(`[Storyblok] Preview token unauthorized. Trying alternative tokens for ${type}.`);

  // If we might have used URL token, try env token instead
  if (mightHaveUsedUrlToken) {
    console.log('[Storyblok] Trying env preview token instead of URL token');
    const envPreviewClient = getStoryblokClient({ forcePreview: true, ignoreUrlToken: true });
    if (envPreviewClient) {
      try {
        // Wrap with retry logic for 429 errors
        const response = await retryStoryblokRequest(() => draftRequest(envPreviewClient), {
          requestType: `${type}-fallback-draft`,
        });
        return response.data || response;
      } catch (previewError) {
        console.warn('[Storyblok] Env preview token also failed:', {
          message: previewError.message,
        });
      }
    }
  }

  // Final fallback to published version
  const fallbackClient = getStoryblokClient(false);
  if (fallbackClient) {
    console.log('[Storyblok] Falling back to published version');
    try {
      // Wrap with retry logic for 429 errors
      const response = await retryStoryblokRequest(() => publishedRequest(fallbackClient), {
        requestType: `${type}-fallback-published`,
      });
      return response.data || response;
    } catch (fallbackError) {
      console.warn('[Storyblok] Published version fallback also failed:', {
        message: fallbackError.message,
      });
    }
  }

  return null;
}

/**
 * Retry Storyblok API request with exponential backoff on 429 errors
 * @param {Function} requestFn - Function that returns a Promise for the API request
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: MAX_RETRIES)
 * @param {number} options.initialDelay - Initial delay in ms (default: INITIAL_RETRY_DELAY)
 * @param {string} options.requestType - Type of request for logging (e.g., 'story', 'stories')
 * @returns {Promise<any>} Response data from successful request
 * @throws {Error} Error after all retries exhausted
 */
async function retryStoryblokRequest(requestFn, options = {}) {
  const {
    maxRetries = MAX_RETRIES,
    initialDelay = INITIAL_RETRY_DELAY,
    requestType = 'request',
  } = options;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // TEMPORARY: Simulate 429 error for testing
      if (SIMULATE_429_ERROR) {
        // Check if we should simulate error for this attempt
        let shouldSimulate = false;
        if (SIMULATE_429_ERROR === true) {
          // Simulate 429 error for all attempts EXCEPT the last one
          // This allows testing retry logic while ensuring the last retry succeeds
          shouldSimulate = attempt < maxRetries;
        } else if (typeof SIMULATE_429_ERROR === 'number') {
          // Simulate for first N attempts, then allow success
          shouldSimulate = attempt < SIMULATE_429_ERROR;
        }

        if (shouldSimulate) {
          console.warn(
            `[Storyblok TEST] ⚠️ Simulating 429 error for ${requestType} request (attempt ${
              attempt + 1
            }/${maxRetries + 1})`,
          );
          const mock429Error = new Error('Rate limit exceeded');
          mock429Error.response = {
            status: 429,
            statusText: 'Too Many Requests',
            headers: {
              'retry-after': '1', // 1 second retry-after for testing
            },
            data: {
              message: 'Rate limit exceeded',
            },
          };
          console.warn('[Storyblok TEST] Throwing mock 429 error:', {
            status: mock429Error.response.status,
            headers: mock429Error.response.headers,
          });
          throw mock429Error;
        }
      }

      const response = await requestFn();
      // If this is a retry after a 429, log success
      if (attempt > 0) {
        console.log(
          `[Storyblok] ${requestType} request succeeded after ${attempt} retry attempt(s)`,
        );
      }
      return response;
    } catch (error) {
      lastError = error;
      const status = error.response?.status;

      console.log(`[Storyblok] Error caught - status: ${status}, attempt: ${attempt + 1}`);

      // Detect network errors (no response status usually means network error)
      const isNetworkError =
        !status &&
        (error?.message?.toLowerCase().includes('network') ||
          error?.code === 'NETWORK_ERROR' ||
          error?.code === 'ERR_NETWORK' ||
          error?.message?.toLowerCase().includes('failed to fetch') ||
          error?.message?.toLowerCase().includes('networkerror'));

      // Detect timeout errors
      const isTimeoutError =
        error?.message?.toLowerCase().includes('timeout') ||
        error?.code === 'TIMEOUT_ERROR' ||
        error?.code === 'ECONNABORTED' ||
        status === 504; // Gateway timeout

      // Detect server errors (5xx except 500 and 503) - might be transient
      // Don't retry on 500 (Internal Server Error) - server is already stressed, retrying adds more load
      // Don't retry on 503 (Service Unavailable) - server is overloaded/maintenance, retrying adds more load
      const isServerError = status > 500 && status < 600 && status !== 503;

      // Retry on: 429 (rate limit), network errors, timeouts, and some 5xx server errors (502, 504, etc.)
      // Don't retry on: 4xx client errors (except 429), authentication errors (401), 500, 503 errors
      const shouldRetry = status === 429 || isNetworkError || isTimeoutError || isServerError;

      if (!shouldRetry) {
        const errorType =
          status === 401 ? 'authentication' : status ? `client error (${status})` : 'unknown';
        console.warn(`[Storyblok] Non-retryable error (${errorType}), throwing immediately`);
        throw error;
      }

      // Determine error type for logging
      let errorType = 'unknown';
      if (status === 429) {
        errorType = 'rate limit (429)';
      } else if (isNetworkError) {
        errorType = 'network error';
      } else if (isTimeoutError) {
        errorType = 'timeout error';
      } else if (isServerError) {
        errorType = `server error (${status})`;
      }

      console.log(
        `[Storyblok] ${errorType} detected, will retry (attempt ${attempt + 1}/${maxRetries + 1})`,
      );

      // Option to fail fast on retryable errors (skip retries for faster page rendering)
      if (FAIL_FAST_ON_429) {
        console.warn(
          `[Storyblok] FAIL_FAST_ON_429 enabled - skipping retries and failing immediately`,
        );
        throw error;
      }

      // If we've exhausted all retries, throw the error
      if (attempt === maxRetries) {
        console.error(
          `[Storyblok] ${requestType} request failed after ${maxRetries} retries due to ${errorType}`,
        );
        throw error;
      }

      // Calculate delay with exponential backoff
      // First retry is immediate (0ms) for fastest recovery
      const exponentialDelay = attempt === 0 ? 0 : initialDelay * Math.pow(2, attempt - 1);

      // Check for Retry-After header (only for 429 errors)
      const retryAfterHeader = status === 429 ? error.response?.headers?.['retry-after'] : null;
      let delay = exponentialDelay;

      // Use Retry-After header if available (value is in seconds)
      // Only applies to 429 rate limit errors
      // Use the longer of Retry-After or exponential backoff to respect server's request
      if (retryAfterHeader) {
        const retryAfterSeconds = parseInt(retryAfterHeader, 10);
        if (!isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
          const retryAfterMs = retryAfterSeconds * 1000;
          // Use the longer delay (server's Retry-After or exponential backoff)
          delay = Math.max(retryAfterMs, exponentialDelay);
          console.log(
            `[Storyblok] Retry-After header: ${retryAfterSeconds}s (${retryAfterMs}ms), Exponential backoff: ${exponentialDelay}ms, Using: ${delay}ms (${(
              delay / 1000
            ).toFixed(2)}s)`,
          );
        }
      } else {
        console.log(
          `[Storyblok] Exponential backoff delay: ${exponentialDelay}ms (${(
            exponentialDelay / 1000
          ).toFixed(2)}s) for attempt ${attempt + 1}/${maxRetries}`,
        );
      }

      // Cap delay at MAX_RETRY_DELAY
      delay = Math.min(delay, MAX_RETRY_DELAY);

      if (delay > 0) {
        const delaySeconds = (delay / 1000).toFixed(2);
        console.warn(
          `[Storyblok] ${errorType} for ${requestType} request. Retrying in ${delay}ms (${delaySeconds}s) - attempt ${
            attempt + 1
          }/${maxRetries}`,
        );
        // Wait before retrying with minimal delay for faster retries
        // Using setTimeout which is non-blocking - allows other operations to continue
        const waitStartTime = Date.now();
        await new Promise(resolve => setTimeout(resolve, delay));
        const waitDuration = Date.now() - waitStartTime;
        console.log(
          `[Storyblok] Wait completed after ${waitDuration}ms (${(waitDuration / 1000).toFixed(
            2,
          )}s) - proceeding with retry ${attempt + 1}/${maxRetries}`,
        );
      } else {
        console.warn(
          `[Storyblok] ${errorType} for ${requestType} request. Retrying immediately (0ms wait) - attempt ${
            attempt + 1
          }/${maxRetries}`,
        );
        // First retry is immediate - use microtask to allow event loop to process other tasks
        await new Promise(resolve => {
          // Use queueMicrotask for immediate but non-blocking retry
          queueMicrotask(() => resolve());
        });
      }
    }
  }

  // This should never be reached, but TypeScript/ESLint might complain
  throw lastError;
}

/**
 * Determine preview version and client configuration
 * @param {string} requestedVersion - Requested version from options
 * @returns {Object} Configuration object with version, client, and fallback flags
 */
function getPreviewConfig(requestedVersion) {
  const isBrowser = typeof window !== 'undefined';
  const isPreviewPath = isBrowser && isPreviewRoute();
  const isVisualEditor = isBrowser && isInStoryblokVisualEditor();

  // Visual editor always uses draft version with URL token
  // Preview routes use draft version (auth handled via HTTP Basic Auth in PreviewLayout)
  const shouldUsePreviewVersion =
    isVisualEditor || isPreviewPath || (!isBrowser && requestedVersion === 'draft');

  const version = shouldUsePreviewVersion ? 'draft' : 'published';
  const allowPreviewFallback = !isBrowser || isVisualEditor;
  const mightHaveUsedUrlToken = isBrowser && getPreviewToken() !== null;

  // Visual editor forces URL token usage (ignoreUrlToken: false)
  const client = getStoryblokClient(
    isVisualEditor ? { forcePreview: true, ignoreUrlToken: false } : shouldUsePreviewVersion,
  );

  return {
    version,
    client,
    shouldUsePreviewVersion,
    allowPreviewFallback,
    mightHaveUsedUrlToken,
  };
}

export async function getStory(storyPath, options = {}) {
  const requestedVersion = options.version || process.env.VUE_APP_STORYBLOK_VERSION || 'published';
  const { version, client, shouldUsePreviewVersion, allowPreviewFallback, mightHaveUsedUrlToken } =
    getPreviewConfig(requestedVersion);

  if (!client) {
    throw new Error('Storyblok client is not configured. Please set VUE_APP_STORYBLOK_TOKEN.');
  }

  const { version: _ignoredVersion, ...restOptions } = options;

  try {
    // StoryblokClient.get() returns { data } or data directly depending on version
    // Wrap with retry logic for 429 errors
    const response = await retryStoryblokRequest(() =>
      client.get(`cdn/stories/${storyPath}`, {
        version,
        ...restOptions,
      }),
    );

    // Handle both response formats
    const data = response.data || response;
    return data;
  } catch (error) {
    if (shouldUsePreviewVersion && allowPreviewFallback && error.response?.status === 401) {
      const fallbackData = await tryPreviewTokenFallback(
        mightHaveUsedUrlToken,
        client => client.get(`cdn/stories/${storyPath}`, { version: 'draft', ...restOptions }),
        client => client.get(`cdn/stories/${storyPath}`, { version: 'published', ...restOptions }),
        'story',
      );
      if (fallbackData) return fallbackData;
    }
    console.error('Error fetching story from Storyblok:', error);
    throw error;
  }
}

// Re-export error message utilities from centralized error.js
export { getErrorMessage } from './error';

/**
 * Extract plain text from Storyblok rich text field
 * Handles both rich text objects and plain strings
 * @param {string|Object} richText - Storyblok rich text field (can be string or object)
 * @returns {string} Plain text extracted from rich text
 */
function extractTextFromRichText(richText) {
  if (!richText) return '';

  // If it's already a string, return it
  if (typeof richText === 'string') {
    // Strip HTML tags if present
    return richText
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // If it's an object (Storyblok rich text format)
  if (typeof richText === 'object' && richText !== null) {
    // Handle Storyblok rich text format with content array
    if (Array.isArray(richText.content)) {
      return richText.content
        .map(block => {
          if (block.content && Array.isArray(block.content)) {
            return block.content.map(text => text.text || '').join('');
          }
          return block.text || '';
        })
        .join(' ')
        .trim();
    }
    // Handle other object formats
    if (richText.text) return richText.text;
    if (richText.content) return String(richText.content);
  }

  return '';
}

/**
 * Fetch multiple stories from Storyblok
 * @param {Object} options - Query options
 * @param {string} options.folder - Folder slug to filter by
 * @param {string} options.contentType - Content type filter (e.g., 'church-update')
 * @param {Object} options.filterQuery - Filter query object (e.g., { isFeatured: { is: true } })
 * @param {string} options.sortBy - Sort field (e.g., 'date:desc')
 * @param {number} options.perPage - Number of stories per page (default: 25)
 * @param {number} options.page - Page number (default: 1)
 * @returns {Promise<Array>} Array of stories
 */
export async function getStories(options = {}) {
  const requestedVersion = options.version || process.env.VUE_APP_STORYBLOK_VERSION || 'published';
  const { version, client, shouldUsePreviewVersion, allowPreviewFallback, mightHaveUsedUrlToken } =
    getPreviewConfig(requestedVersion);

  if (!client) {
    throw new Error('Storyblok client is not configured. Please set VUE_APP_STORYBLOK_TOKEN.');
  }

  const perPage = options.perPage || 25;
  const page = options.page || 1;

  const params = {
    version,
    per_page: perPage,
    page,
  };

  // Add folder filter if specified
  // Storyblok requires trailing slash for folder paths
  if (options.folder) {
    params.starts_with = options.folder.endsWith('/') ? options.folder : `${options.folder}/`;
  }

  // Add content type filter
  if (options.contentType) {
    params.filter_query = {
      ...params.filter_query,
      component: { in: options.contentType },
    };
  }

  // Add custom filter query
  if (options.filterQuery) {
    params.filter_query = {
      ...params.filter_query,
      ...options.filterQuery,
    };
  }

  // Add sort
  if (options.sortBy) {
    params.sort_by = options.sortBy;
  }

  // Log the request parameters for debugging
  if (options.folder === 'church-updates') {
    console.log('[Storyblok] Request params:', JSON.stringify(params, null, 2));
  }

  try {
    // Wrap with retry logic for 429 errors
    const response = await retryStoryblokRequest(() => client.get('cdn/stories', params), {
      requestType: 'stories',
    });

    // Handle both response formats
    const data = response.data || response;
    const stories = data.stories || [];

    return stories;
  } catch (error) {
    if (shouldUsePreviewVersion && allowPreviewFallback && error.response?.status === 401) {
      const fallbackData = await tryPreviewTokenFallback(
        mightHaveUsedUrlToken,
        client => client.get('cdn/stories', { ...params, version: 'draft' }),
        client => client.get('cdn/stories', { ...params, version: 'published' }),
        'stories',
      );
      if (fallbackData) {
        const data = fallbackData.data || fallbackData;
        return data.stories || [];
      }
    }
    console.error('Error fetching stories from Storyblok:', error);
    throw error;
  }
}

/**
 * Fetch featured church updates from Storyblok
 * Fetches from church-updates folder and filters by isFeatured = true
 * @returns {Promise<Array>} Array of featured church updates
 */
export async function getFeaturedChurchUpdates() {
  try {
    console.log('[Storyblok] Fetching featured church updates...');

    // Fetch featured stories with filter
    // Note: Cannot sort by custom 'date' field in Storyblok API, so we'll sort in JavaScript
    // Component name in Storyblok is "Church Update Page" (with spaces and capitals)
    const stories = await getStories({
      folder: 'church-updates',
      contentType: 'Church Update Page',
      filterQuery: {
        isFeatured: { is: true },
      },
      // Don't use sortBy - Storyblok doesn't support sorting by custom fields
      // We'll sort by date field in JavaScript after fetching
      perPage: 25, // Fetch more to ensure we have enough after filtering
    });

    console.log('[Storyblok] Featured stories fetched:', {
      count: stories.length,
      stories: stories.map(s => ({
        name: s.name,
        slug: s.slug,
        isFeatured: s.content?.isFeatured,
        hasThumbnail: !!(s.content?.thumbnail || s.content?.homepageThumbnail),
      })),
    });

    // Map Storyblok stories to app format
    const mappedStories = stories.map(story => {
      const content = story.content || {};
      const thumbnail = content.thumbnail || {};
      const homepageThumbnail = content.homepageThumbnail || {};

      // homepageThumbnail is the landscape/widescreen variant for homepage cards.
      const homepageAsset =
        content.isFeatured && (homepageThumbnail.filename || homepageThumbnail.url);
      const thumbnailAsset = thumbnail.filename || thumbnail.url;

      return {
        slug: content.slug || story.slug || story.full_slug || '',
        title: content.title || story.name || '',
        date: content.date || story.first_published_at || story.published_at || '',
        description: extractTextFromRichText(content.description),
        thumbnail: {
          // S3-first per surface, preserving the landscape homepage image when a
          // story has only been partially migrated:
          //   1. homepageThumbnailUrl (S3 landscape)
          //   2. homepageThumbnail asset (native landscape, featured only)
          //   3. thumbnailUrl (S3 card)
          //   4. thumbnail asset (native card)
          // The landscape asset (2) outranks the generic card URL (3) so a
          // featured story keeps its widescreen image until homepageThumbnailUrl
          // is also set.
          url:
            content.homepageThumbnailUrl?.trim() ||
            homepageAsset ||
            content.thumbnailUrl?.trim() ||
            thumbnailAsset ||
            '',
        },
        isExternalLink: content.isExternalLink || false,
        isNew: content.isNew || false,
        showShareButton: canShareChurchUpdate(content),
        church_update_tags: content.church_update_tags || [],
      };
    });

    // Sort by date field (descending) in JavaScript since Storyblok can't sort by custom fields
    mappedStories.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });

    // Return top 10 after sorting
    const result = mappedStories.slice(0, 10);
    console.log('[Storyblok] Mapped and sorted featured updates:', {
      count: result.length,
      updates: result.map(u => ({ title: u.title, slug: u.slug, date: u.date })),
    });
    return result;
  } catch (error) {
    console.error('[Storyblok] Error fetching featured church updates:', error);
    console.error('[Storyblok] Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
}

export default getStoryblokClient;
